/**
 * In-flight request dedup + per-caller abort, for the fetch-adapter
 * layer that feeds ``DashboardBody`` (``DashboardDataSource`` and the
 * builder's ``previewApi``).
 *
 * Why this exists (PR #45 review remediation):
 *
 *   - React 18 StrictMode double-invokes every mount effect in dev, so a
 *     naive "fetch in effect, abort in cleanup" pattern issues each
 *     preview request twice per mount (once aborted, once real) for
 *     EVERY family — filters / kpis / charts / table / drill. Component
 *     level dedup guards (last-key refs, mounted refs) fixed one family
 *     at a time and made the effects hard to reason about.
 *   - Deduping at the CLIENT (keyed on url + body) collapses identical
 *     concurrent requests into one network call for all families at
 *     once, and lets the components go back to the plain
 *     AbortController-per-effect-generation pattern — which is what
 *     actually kills the A→B→A stale-response race at the root.
 *
 * Semantics:
 *
 *   1. A call whose ``key`` matches an in-flight request REUSES that
 *      request's promise (no second network call).
 *   2. The map entry is cleared when the request settles — success or
 *      failure. Never sticky: a failed request is immediately
 *      retryable with the same key.
 *   3. Each caller may pass its own ``AbortSignal``. Aborting it
 *      rejects THAT caller's promise with an ``AbortError``
 *      immediately, without cancelling the shared request while other
 *      subscribers remain.
 *   4. When the LAST subscriber aborts, the underlying request is
 *      aborted after one macrotask. The deferral is what lets
 *      StrictMode's synchronous cleanup→re-setup re-subscribe to the
 *      same flight (one network call per mount); a genuine
 *      navigate-away (no re-subscribe) really cancels on the next
 *      tick. Callers that never passed a signal count as permanent
 *      subscribers — the flight is never cancelled from under them.
 */

/** Launches the underlying request, wired to the SHARED abort signal. */
export type DedupedStart<T> = (signal: AbortSignal) => Promise<T>;

export interface InFlightDeduper {
  <T>(key: string, start: DedupedStart<T>, signal?: AbortSignal): Promise<T>;
  /** Requests currently in flight — test/debug introspection only. */
  size(): number;
}

interface InFlightEntry {
  promise: Promise<unknown>;
  controller: AbortController;
  /** Live subscribers. Only signal-bearing callers ever release. */
  refs: number;
  /** Deferred underlying-abort timer, armed when ``refs`` hits 0. */
  abortTimer: ReturnType<typeof setTimeout> | null;
}

function abortError(): DOMException {
  return new DOMException("The request was aborted.", "AbortError");
}

export function createInFlightDeduper(): InFlightDeduper {
  const inFlight = new Map<string, InFlightEntry>();

  function release(key: string, entry: InFlightEntry): void {
    entry.refs -= 1;
    if (entry.refs > 0 || inFlight.get(key) !== entry) return;
    // Deferred one macrotask: StrictMode's dev unmount/remount aborts and
    // re-issues the same request synchronously — give the re-subscribe a
    // chance to reuse the flight before really cancelling it.
    entry.abortTimer = setTimeout(() => {
      entry.abortTimer = null;
      if (entry.refs <= 0 && inFlight.get(key) === entry) {
        inFlight.delete(key);
        entry.controller.abort();
      }
    }, 0);
  }

  function dedupe<T>(
    key: string,
    start: DedupedStart<T>,
    signal?: AbortSignal,
  ): Promise<T> {
    let entry = inFlight.get(key);
    if (!entry) {
      const controller = new AbortController();
      const created: InFlightEntry = {
        promise: Promise.resolve(), // replaced synchronously below
        controller,
        refs: 0,
        abortTimer: null,
      };
      created.promise = start(controller.signal).finally(() => {
        // Clear on settle — success AND failure — so nothing is sticky.
        if (inFlight.get(key) === created) inFlight.delete(key);
      });
      // If every subscriber aborts before the shared request settles,
      // nobody is listening on the shared promise any more — swallow so
      // the eventual rejection doesn't surface as an unhandled rejection.
      // Subscribers still receive real errors through their own derived
      // promises below.
      created.promise.catch(() => undefined);
      inFlight.set(key, created);
      entry = created;
    } else if (entry.abortTimer !== null) {
      // A re-subscribe arrived inside the deferral window — keep flying.
      clearTimeout(entry.abortTimer);
      entry.abortTimer = null;
    }

    const shared = entry;
    shared.refs += 1;

    if (!signal) {
      // No caller signal → permanent subscriber; shares the raw promise.
      return shared.promise as Promise<T>;
    }
    if (signal.aborted) {
      release(key, shared);
      return Promise.reject(abortError());
    }

    // Subscriber-scoped promise: rejects the moment THIS caller aborts;
    // the shared flight keeps going while other subscribers remain.
    return new Promise<T>((resolve, reject) => {
      const onAbort = (): void => {
        release(key, shared);
        reject(abortError());
      };
      signal.addEventListener("abort", onAbort, { once: true });
      shared.promise.then(
        (value) => {
          signal.removeEventListener("abort", onAbort);
          resolve(value as T);
        },
        (err: unknown) => {
          signal.removeEventListener("abort", onAbort);
          reject(err);
        },
      );
    });
  }

  dedupe.size = (): number => inFlight.size;
  return dedupe;
}
